/**
 * MeerwerkGoedkeurenModal — lightweight confirm-before-billing dialog for a
 * single pending meerwerk in the foto-controle reviewer flow.
 *
 * Deliberately NOT a wrapper around GoedkeurenModal (that component's shape is
 * collector/tier-specific). This one shows type, qty, OA name, and an editable
 * €/unit input (werkuren/overig) or a read-only server rate display (km).
 *
 * The parent calls onConfirm and closes the modal on success; errors from
 * onConfirm are shown inline as a result banner.
 */
import { useState } from 'react';
import {
  MEERWERK_TYPE_LABELS,
  MEERWERK_UNIT_LABELS,
} from '@/api/meerwerken';
import type { ReviewMeerwerk } from '@/api/tasks';
import { InterventieBadge } from '@/components/interventie/InterventieBadge';
import { GeenPrijsLabel } from '@/components/ui/GeenPrijsLabel';

export interface MeerwerkGoedkeurenModalProps {
  meerwerk: ReviewMeerwerk;
  onCancel: () => void;
  /** Called with opts on confirm. Throws on API error (shown as inline banner). */
  onConfirm: (opts: { unitPrice?: number }) => Promise<void>;
}

export function MeerwerkGoedkeurenModal({ meerwerk, onCancel, onConfirm }: MeerwerkGoedkeurenModalProps) {
  const isKm = meerwerk.type === 'kilometervergoeding';

  // For werkuren/overig: editable price input; pre-filled from stored unit_price.
  const [unitPriceStr, setUnitPriceStr] = useState(String(meerwerk.unit_price));
  const [submitting, setSubmitting] = useState(false);
  const [resultBanner, setResultBanner] = useState<{ type: 'error'; message: string } | null>(null);

  const localUnitPrice = parseFloat(unitPriceStr);
  const isValidPrice = !isNaN(localUnitPrice);
  // Red warning when the price is ≤ 0 (but confirm is never blocked).
  const isUnpriced = !isKm && isValidPrice && localUnitPrice <= 0;

  // Line total shown in the modal for non-km types.
  const lineTotal = isValidPrice && meerwerk.quantity > 0 ? meerwerk.quantity * localUnitPrice : null;
  // km line total uses the server price; none when the server has no price for it.
  const kmPrice = meerwerk.effective_unit_price;
  const kmTotal = isKm && kmPrice !== null && meerwerk.quantity > 0 ? meerwerk.quantity * kmPrice : null;

  const typeLabel = MEERWERK_TYPE_LABELS[meerwerk.type] ?? meerwerk.type;
  const unitLabel = meerwerk.unit ? (MEERWERK_UNIT_LABELS[meerwerk.unit] ?? meerwerk.unit) : null;

  const handleConfirm = async () => {
    setSubmitting(true);
    setResultBanner(null);
    try {
      // Pass unitPrice only for werkuren/overig; km price is server-fixed.
      const opts: { unitPrice?: number } = isKm
        ? {}
        : { unitPrice: isValidPrice ? localUnitPrice : undefined };
      await onConfirm(opts);
      // On success the caller closes the modal (removes it from the DOM).
    } catch (err) {
      setResultBanner({
        type: 'error',
        message: err instanceof Error ? err.message : 'Goedkeuren mislukt',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={submitting ? undefined : onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mw-goed-modal-title"
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 pb-4 border-b border-gray-100">
          <div>
            <h3
              id="mw-goed-modal-title"
              className="text-lg font-extrabold text-gray-900"
            >
              Meerwerk goedkeuren
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">{meerwerk.name}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none disabled:opacity-50"
            aria-label="Sluiten"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto space-y-4">
          {/* Type + quantity badges */}
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="px-2 py-0.5 bg-td-blue/10 text-td-blue rounded-full font-medium">
              {typeLabel}
            </span>
            {meerwerk.quantity > 0 && unitLabel && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full font-medium">
                {meerwerk.quantity} {unitLabel.toLowerCase()}
              </span>
            )}
          </div>

          {/* OA name */}
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-900">
            Wordt toegevoegd aan de lopende PO van{' '}
            <span className="font-semibold">{meerwerk.subcontractor_name ?? 'onderaannemer'}</span>.
          </div>

          {/* Date / task / note meta */}
          {(meerwerk.date || meerwerk.task_name || meerwerk.note) && (
            <div className="text-xs text-gray-500 space-y-0.5">
              {meerwerk.date && <div>{meerwerk.date}</div>}
              {meerwerk.task_name && (
                <div>{meerwerk.task_name}{meerwerk.task_is_interventie && <InterventieBadge className="ml-1.5 align-middle" />}</div>
              )}
              {meerwerk.note && <div className="text-gray-400">{meerwerk.note}</div>}
            </div>
          )}

          {/* Price section */}
          {isKm ? (
            /* km: server-determined rate — read only */
            <div className="rounded-lg border border-gray-200 px-4 py-3 text-sm">
              <div className="text-xs font-semibold text-gray-500 mb-1">Tarief (server-berekend)</div>
              {kmPrice === null ? (
                <GeenPrijsLabel reason={meerwerk.price_unavailable_reason} className="block text-sm" />
              ) : (
                <div className="text-gray-900 font-medium">
                  € {kmPrice.toFixed(2)} / km
                  {kmTotal !== null && (
                    <span className="text-gray-500 font-normal ml-2">
                      = € {kmTotal.toFixed(2)}
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* werkuren / overig: editable price input */
            <div className="rounded-lg border border-gray-200 px-4 py-3 space-y-2">
              <div>
                <label
                  htmlFor="mw-unit-price"
                  className="block text-xs font-semibold text-gray-500 mb-1"
                >
                  €/eenheid
                </label>
                <input
                  id="mw-unit-price"
                  type="number"
                  step="any"
                  min="0"
                  value={unitPriceStr}
                  onChange={(e) => setUnitPriceStr(e.target.value)}
                  disabled={submitting}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue disabled:opacity-60 ${
                    isUnpriced
                      ? 'border-red-300 text-red-600 bg-red-50'
                      : 'border-gray-300 text-gray-900 bg-white'
                  }`}
                />
                {isUnpriced && (
                  <p className="mt-1 text-xs text-red-600">
                    Geen prijs ingesteld — toch goedkeuren?
                  </p>
                )}
              </div>
              {lineTotal !== null && (
                <div
                  className={`text-sm font-medium ${isUnpriced ? 'text-red-600' : 'text-gray-900'}`}
                >
                  Totaal: € {lineTotal.toFixed(2)}
                </div>
              )}
            </div>
          )}

          {/* Error from onConfirm */}
          {resultBanner && (
            <div
              className="rounded-lg px-4 py-3 text-sm bg-red-50 border border-red-200 text-red-800"
              role="alert"
            >
              {resultBanner.message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm font-semibold bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-60"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="px-4 py-2 text-sm font-semibold bg-td-blue text-white rounded-lg hover:bg-td-blue/90 transition disabled:opacity-60 inline-flex items-center gap-2"
          >
            {submitting && (
              <svg
                className="animate-spin h-4 w-4"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}
            {submitting ? 'Bezig…' : 'Goedkeuren & op PO'}
          </button>
        </div>
      </div>
    </div>
  );
}
